import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [currentMetric, setCurrentMetric] = useState(0);
  const [animatedCounts, setAnimatedCounts] = useState({
    projects: 0,
    satisfaction: 0,
    responseTime: 0
  });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const metrics = [
    { label: "Projects Completed", value: 5000, suffix: "+" },
    { label: "Client Satisfaction", value: 98, suffix: "%" },
    { label: "Response Time", value: 24, suffix: "hrs" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % metrics.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        
        setAnimatedCounts({
          projects: Math.floor(5000 * progress),
          satisfaction: Math.floor(98 * progress),
          responseTime: Math.floor(24 * progress)
        });

        if (step >= steps) {
          clearInterval(timer);
          setAnimatedCounts({
            projects: 5000,
            satisfaction: 98,
            responseTime: 24
          });
        }
      }, stepDuration);
    };

    const timer = setTimeout(animateCounters, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleGetStarted = () => {
    const element = document.getElementById('contact');
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleViewPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Enhanced Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden particles">
        {/* Main blob animations */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animate-blob-delay-2"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animate-blob-delay-4"></div>
        
        {/* Additional floating elements */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-r from-cyan-300 to-blue-400 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-morph"></div>
        <div className="absolute bottom-1/4 left-1/3 w-48 h-48 bg-gradient-to-r from-green-300 to-teal-400 rounded-full mix-blend-multiply filter blur-2xl opacity-25 animate-float-slow"></div>

        {/* Gradient overlay with animation */}
        <div className="absolute inset-0 bg-gradient-animated opacity-5 animate-gradient-xy"></div>
      </div>

      {/* Enhanced Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

      {/* Parallax floating elements */}
      <div 
        className="absolute top-20 left-10 w-16 h-16 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full opacity-60 animate-float"
        style={{
          transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
        }}
      ></div>
      <div 
        className="absolute bottom-20 right-10 w-12 h-12 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full opacity-60 animate-float-delay-1"
        style={{
          transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)`,
        }}
      ></div>
      <div 
        className="absolute top-1/2 right-20 w-8 h-8 bg-gradient-to-r from-yellow-200 to-orange-200 rounded-full opacity-60 animate-float-delay-2"
        style={{
          transform: `translate(${mousePosition.x * 0.015}px, ${mousePosition.y * 0.015}px)`,
        }}
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center ">
        {/* Main Hero Content */}
        <div className="max-w-4xl mx-auto">
          {/* Enhanced Badge */}
          <div className="inline-flex items-center px-6 py-3 rounded-full glass text-blue-800 text-sm font-medium mb-8 animate-bounce-in shadow-glow">
            <Icon name="Zap" size={16} className="mr-2 animate-pulse-glow" />
            <span className="text-gradient">Trusted by 5000+ businesses worldwide</span>
          </div>

          {/* Enhanced Main Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-poppins text-gray-900 mb-6 leading-tight animate-slide-in-up">
            Your One-Stop
            <span className="block text-gradient-rainbow animate-text-shimmer bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-[length:200%_auto]">
              Freelance Solution
            </span>
          </h1>

          {/* Enhanced Subheadline */}
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed animate-slide-in-up [animation-delay:200ms]">
            Access vetted professionals with transparent pricing, guaranteed quality, and streamlined project management.
          </p>

          {/* Enhanced CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-slide-in-up [animation-delay:400ms]">
            <Button
              variant="default"
              size="lg"
              onClick={handleGetStarted}
              className="font-poppins font-semibold shadow-glow-lg text-lg px-8 py-4 btn-modern hover-lift transform hover:scale-105 transition-all duration-300"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Get Started Today
            </Button>
            {/* <Button
              variant="outline"
              size="lg"
              onClick={handleViewPricing}
              className="font-poppins font-semibold text-lg px-8 py-4 glass border-0 hover:glass-dark transition-all duration-300 hover-lift"
              iconName="DollarSign"
              iconPosition="left"
            >
              View Pricing
            </Button> */}
          </div>

          {/* Enhanced Trust Metrics Bar */}
          <div className="glass rounded-3xl shadow-multi border-0 p-8 animate-slide-in-up [animation-delay:600ms] hover-lift">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2 group-hover:animate-pulse-glow transition-all duration-300">
                  {animatedCounts.projects.toLocaleString()}+
                </div>
                <div className="text-gray-600 font-medium">Projects Completed</div>
              </div>
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2 group-hover:animate-pulse-glow transition-all duration-300">
                  {animatedCounts.satisfaction}%
                </div>
                <div className="text-gray-600 font-medium">Client Satisfaction</div>
              </div>
              <div className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2 group-hover:animate-pulse-glow transition-all duration-300">
                  {animatedCounts.responseTime}hrs
                </div>
                <div className="text-gray-600 font-medium">Response Time</div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Scroll Indicator */}
        {/* <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-float">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center glass">
            <div className="w-1 h-3 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full mt-2 animate-glow"></div>
          </div>
        </div> */}
      </div>

      {/* Additional decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-20 animate-particle-float`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;